import type { TranslationSchema } from '../pt-BR';
import type { DeepPartialStrings } from '../types';

export const pro = {
    common: {
        notNow: 'Not now',
        restorePurchase: 'Restore purchase',
        seeAllPlans: 'See all plans',
        opening: 'Opening...',
        pleaseWait: 'Please wait...',
        popularBadge: 'MOST POPULAR',
        trialFallbackPeriod: '7 days',
        startTrialCta: 'Start {{period}} free',
        startTrialCtaPlain: 'Start free trial',
        trialReassurance: '{{price}} today · we\'ll remind you on day {{day}} · cancel anytime',
        termsOfUse: 'Terms of Use',
        privacyPolicy: 'Privacy Policy',
    },
    plans: {
        monthly: { title: 'Monthly', period: '/month' },
        annual: { title: 'Annual', period: '/year', tagline: 'Best value' },
        lifetime: { title: 'Lifetime', period: 'once' },
    },
    features: {
        chart: {
            title: 'Advanced reports with PDF',
            description: 'Fixed vs variable, spending by day of the week, budgeted vs actual, and PDFs ready to share.',
        },
        forecast: {
            title: 'Cash flow',
            description: 'Track money in and out over time and see where your money is going.',
        },
        compare: {
            title: 'Period comparison',
            description: 'Compare months side by side and see how your spending and income evolve.',
        },
        scales: {
            title: 'Net result by category',
            description: 'How much each category really weighed this month: money in minus money out, no fooling yourself.',
        },
        table: {
            title: 'Export to spreadsheet',
            description: 'Download a monthly summary by account or all transactions in CSV for Excel and Google Sheets.',
        },
        handcoins: {
            title: 'Owed to me',
            description: 'Record what someone owes you — a loan, a sale, a split bill — and follow the balance per person and the agreed dates, without mixing them with your spending.',
        },
        import: {
            title: 'Statement import',
            description: 'Bring in your history from CSV, OFX, PDF, or statement screenshots — it adds to what you already have, without erasing anything.',
        },
        reconcile: {
            title: 'Unlimited statement check',
            description: 'Compare your card statement with the app, find what\'s missing or off, and log it all in one tap.',
        },
        bell: {
            title: 'Custom reminders',
            description: 'Choose due-date times and inactivity intervals. Turning them on and off stays free.',
        },
        widget: {
            title: 'Home screen widgets',
            description: 'Month balance, income and expenses at a glance, without opening the app.',
        },
        lightning: {
            title: 'Home shortcuts',
            description: 'Build a row of shortcuts to the areas you use most day to day.',
        },
        house: {
            title: 'Home screen your way',
            description: 'Reorder the home blocks and show or hide what you want to see first.',
        },
        calendar: {
            title: 'A month that starts on your day',
            description: 'From payday to the day before the next one: Home, Transactions, Analysis and reports follow your period, not the calendar month.',
        },
        palette: {
            title: 'Themes and appearance',
            description: 'Ten light and dark palettes plus an accent color to make Kontto your own.',
        },
        sync: {
            title: 'Sync across devices',
            description: 'Use the same account on phone and tablet — accounts, transactions, and everything else stay in sync on both, end-to-end encrypted.',
        },
        target: {
            title: 'Everything unlimited',
            description: 'Create accounts, cards, categories, tags, goals, and budgets with no cap to make Kontto your way.',
        },
        folder: {
            title: 'Automatic backup to your folder',
            description: 'An encrypted backup is saved on its own to a folder on your device, with a history of the latest versions.',
        },
        star: {
            title: 'Support an independent developer',
            description: 'Kontto is made by one person. Your subscription keeps the project alive.',
        },
    },
    purchase: {
        unavailableTitle: 'Purchases unavailable',
        unavailableOpenMessage: 'Couldn\'t open the purchase right now. Try again in a moment.',
        unavailableTrialMessage: 'Couldn\'t start the trial right now. Try again in a moment.',
        restored: 'Purchases restored',
        nothingToRestore: 'Nothing to restore',
        noActivePurchaseMessage: 'We couldn\'t find an active purchase on this account.',
        restoreFailed: 'Couldn\'t restore',
    },
    period: {
        day_one: '{{count}} day',
        day_other: '{{count}} days',
        week_one: '{{count}} week',
        week_other: '{{count}} weeks',
        month_one: '{{count}} month',
        month_other: '{{count}} months',
        year_one: '{{count}} year',
        year_other: '{{count}} years',
    },
    paywall: {
        heroTitle: 'Total control.\nEven the price.',
        heroSubtitle: 'Reports, cash flow, and everything unlimited.',
        entryBadge: 'INTRO OFFER',
        trialBadge: '{{period}} FREE',
        annualTitle: 'Annual',
        annualEntrySubtitle: 'First year for {{intro}} · then {{full}}/year',
        annualSubtitle: 'One {{price}} charge per year. That\'s it.',
        perFirstYear: '/1st year',
        entrySavings: '{{pct}}% off first year',
        perMonth: '/month',
        annualSavings: '{{pct}}% cheaper than monthly',
        perYear: '/year',
        monthlyTitle: 'Monthly',
        monthlySubtitle: 'Flexible · cancel anytime',
        founderBadge: 'FOUNDER',
        lifetimeTitle: 'Lifetime',
        lifetimeSubtitle: 'Pay once, never again.',
        founderNote: 'Founder price — going up',
        onceForever: 'once, forever',
        ctaLifetime: 'Buy Lifetime · {{price}}',
        ctaEntry: 'Get it · {{price}} first year',
        ctaAnnual: 'Subscribe · {{price}}/year',
        ctaMonthly: 'Subscribe · {{price}}/month',
        footerLifetime: 'One-time payment. No renewal, no future charges.',
        footerEntry: '{{intro}} today, for the first year · then renews at {{full}}/year until you cancel in {{location}}.',
        footerTrialNoReminder: '{{price}} today · cancel anytime',
        footerAnnual: 'Renews at {{price}}/year until you cancel in {{location}}.',
        footerMonthly: 'Renews at {{price}}/month until you cancel in {{location}}.',
        trust: {
            priceLockedStrong: 'Locked-in price.',
            priceLockedRest: 'It won\'t go up after you subscribe.',
            noSurpriseStrong: 'No surprises.',
            noSurpriseRest: 'We warn you before any charge.',
            noLockStrong: 'No lock-in.',
            noLockRest: 'Cancel in 2 taps, on {{store}}.',
        },
    },
    // "I have a coupon" on the paywall (ADR 0209).
    coupon: {
        link: 'I have a coupon',
        title: 'I have a coupon',
        subtitle: 'Enter the word you received.',
        placeholder: 'Coupon word',
        apply: 'Apply',
        checking: 'Checking...',
        invalid: "We couldn't find this coupon. Check the word.",
        expired: 'This coupon ended on {{date}}.',
        seePlans: 'See Pro plans',
        offline: 'No internet. Connect to check the coupon.',
        unavailable: "We couldn't check the coupon right now. Try again in a moment.",
        retry: 'Try again',
        storeDownTitle: "{{store}} didn't respond",
        storeDownBody: "The coupon is valid, but prices didn't load. Try again in a moment.",
        proTitle: "You're already Pro",
        proBody: "{{code}} is for people who don't have Kontto Pro yet. Your plan stays the same.",
        gotIt: 'Got it',
        applied: 'Coupon {{code}} applied',
        validUntil: 'Valid until {{date}}',
        remove: 'Remove',
        badge: 'COUPON -{{pct}}%',
        badgeNoPct: 'COUPON',
        confirmInAppStore: 'You confirm in the App Store',
        ctaAppStore: 'Continue in the App Store',
        footerAppStore: "We'll open the App Store with the coupon filled in. Confirm there and come back: Pro will be on.",
        notConfirmed: "We haven't seen the confirmation yet. If you already confirmed in the App Store, it takes a few seconds.",
        openAgain: 'Open the App Store again',
        datePattern: 'MM/dd',
    },
    // Founding Member card: top of the plan screen for lifetime owners.
    founderCard: {
        title: 'Founding Member',
        lifetime: 'Lifetime',
        lifetimeSince: 'Lifetime · since {{date}}',
        // date-fns pattern for "since" (renders "Sep 2026").
        sinceDatePattern: 'MMM yyyy',
        promise: 'No monthly fee.\nNo renewal. Forever.',
    },
    manage: {
        title: 'Manage your plan',
        currentPlan: 'Current plan',
        annualPerMonth: '≈ {{price}}/month',
        annualPerMonthSavings: '≈ {{price}}/month • save {{pct}}%',
        manageStorePurchases: 'Manage store purchases',
        recurringNote: 'Recurring charge. Cancel anytime.',
        orPayOnce: 'or pay once',
        lifetimeOptionTitle: '{{plan}} — pay once',
        lifetimeOptionSubtitle: 'Forever, no renewal. Founding Member price — going up.',
        manageOrCancel: 'Manage or cancel in the store',
        featuresTitle: 'What Kontto Pro includes',
        ctaLifetime: 'Buy Lifetime',
        ctaSwitch: 'Switch to {{plan}}',
        fineprintLifetime: 'One-time payment, no renewal.',
        fineprintRenews: '{{planLabel}} · renews automatically until you cancel in {{location}}.',
        lifetimeToastTitle: 'Lifetime activated 💛',
        lifetimeToastMessage: 'Cancel your current subscription in {{location}} so you\'re not charged again.',
        lifetimeSubRenewsTitle: 'Your subscription still renews',
        lifetimeSubTrialRenewsBody:
            'Your free trial of the recurring plan is active until {{date}} — and when it ends, {{store}} will charge the subscription. It won\'t cancel itself: cancel it in {{location}}.',
        lifetimeSubPaidRenewsBody:
            'Your recurring subscription is active until {{date}} and will renew. It won\'t cancel itself: cancel it in {{location}} so you don\'t pay again.',
        lifetimeSubCancelCta: 'Cancel in {{store}}',
        lifetimeSubEndingTitle: 'All good here',
        lifetimeSubTrialEndingBody:
            'Your free trial ends on {{date}} with no charge. Lifetime stays — nothing to do.',
        lifetimeSubPaidEndingBody:
            'Your subscription ends on {{date}} and won\'t renew. Lifetime stays — nothing to do.',
        planUpdated: 'Plan updated',
        openStoreFailed: 'Couldn\'t open',
    },
    offer: {
        expensesFallback: 'Your expenses',
        expenses_one: 'Your {{count}} expense',
        expenses_other: 'Your {{count}} expenses',
        forecastTitle: 'Your cash flow\nalready exists. It\'s Pro.',
        forecastSubtitle: 'Money in and out from your transactions over time — where your money is going. {{trial}} free to see yours.',
        forecastCta: 'See my cash flow — {{trial}} free',
        exportTitle: 'Your spreadsheet\nis ready. It\'s Pro.',
        exportSubtitle: '{{expenses}} in CSV for Excel and Google Sheets, with a monthly summary by account. {{trial}} free to download yours.',
        // Long CTAs dropped the "— {{trial}} free" (mirrors pt-BR): past ~30 chars the
        // label shrank to autoFitFontSize's floor. Trial still shown in the subtitle
        // and in the reassurance line right under the button.
        exportCta: 'Export my spreadsheet',
        bellSubFallback: 'Your subscription',
        bellTitleNext: '{{name}} renews {{date}}.\nWant a reminder 1 day before?',
        bellTitle: '{{name}} will renew.\nWant a reminder 1 day before?',
        bellSubtitle: 'Pro reminds you 1 day before each charge — {{name}} and the ones after. No more forgotten subscriptions on your statement.',
        bellCta: 'Turn on reminders — {{trial}} free',
        backupTitle: '{{transactions}} transactions in {{days}} days.\nAll on a single device.',
        backupSubtitle: 'If your phone breaks or goes missing, your history comes with you. Pro saves an automatic encrypted backup, with version history — without you remembering a thing.',
        backupCta: 'Protect my history',
        importTitle: 'Years of history,\ninside Kontto in minutes.',
        importSubtitle: 'Import CSV, OFX, a bank statement PDF, or even screenshots — the transactions add to what you already have, without deleting anything. {{trial}} free to bring yours in.',
        importCta: 'Import my history',
        reconcileTitle: 'Does the statement add up?\nKontto checks it for you.',
        reconcileSubtitle: 'Send your card statement (PDF or screenshots) and instantly see what\'s missing or off — and fix it all in one tap. {{trial}} free to check yours.',
        reconcileCta: 'Check my statement',
        reportTitle: 'Your report already exists.\nIt\'s Pro.',
        reportSubtitleOne: '{{expenses}} becomes this: categories, comparison to the previous month, and PDF. {{trial}} free to see yours.',
        reportSubtitleMany: '{{expenses}} become this: categories, comparison to the previous month, and PDF. {{trial}} free to see yours.',
        reportCta: 'See my report',
        recordedInMonth: 'You recorded in {{month}}',
        inExpenses_one: 'in {{count}} expense',
        inExpenses_other: 'in {{count}} expenses',
        renewsOn: 'renews {{date}}',
        renewalNote: 'After the trial: Pro Annual for {{price}}/year, renewed automatically until you cancel.',
    },
    proWelcome: {
        closeA11y: 'Close',
        title: 'Welcome to Kontto Pro! 🎉',
        subtitle: 'Everything unlocked. Thank you for supporting an independent developer 💛',
        moreFeatures: 'See the other {{extra}} features',
        notifBlockedText: 'To remind you before any charge and on due dates, turn on notifications.',
        notifEnableButton: 'Enable',
        startButton: 'Get started',
    },
    welcomeOffer: {
        ctaSubscribe: 'Subscribe to Pro Annual',
        ctaSubscribeWithPrice: 'Subscribe to Pro Annual · {{price}}',
        activationTitle: 'That was 1.\nNow see the whole month.',
        activationSubtitleLead: '{{lead}} — Pro turns your expenses into this. {{trial}} free.',
        activationSubtitle: 'Pro turns your expenses into reports and cash flow. {{trial}} free.',
        heroTitle: '{{trial}} of Pro,\nno catch',
        heroSubtitle: 'See exactly how the trial works.',
        hardTitle: 'Your Kontto is ready.\nStart with {{trial}} free.',
        hardSubtitle: 'Everything unlocked today for {{price}}. Here\'s how it works:',
        hardNoTrialTitle: 'Your Kontto is ready.\nSubscribe and unlock everything.',
        hardSeeAllPlans: 'See all plans',
        hardHelp: 'Need help?',
        noTrialTitle: 'Kontto Pro,\nno catch',
        noTrialSubtitle: 'You\'ve already used your trial period. The subscription unlocks everything now — annual renewal, cancel anytime.',
        conversionFull: '{{perMonth}}/month, charged {{annual}} once a year. Cancel before and you pay nothing.',
        conversionAnnual: 'Charged {{annual}} once a year. Cancel before and you pay nothing.',
        conversionBare: 'Cancel before and you pay nothing.',
        timelineTodayTitle: 'Today — everything unlocked',
        timelineTodayTextNoTrial: 'Reports, PDF, cash flow, and no limits, right away.',
        timelineTodayText: 'Reports, PDF, cash flow, and no limits. {{price}}.',
        timelineRenewalTitle: 'Annual renewal',
        timelineRenewalTextFull: '{{perMonth}}/month, charged {{annual}} once a year. Cancel anytime.',
        timelineRenewalText: 'Charged once a year. Cancel anytime.',
        timelineReminderTitle: 'Day {{day}} — we\'ll remind you',
        timelineReminderText: 'A reminder that your trial is ending. No surprise on your statement.',
        timelineConvertTitle: 'Day {{day}} — becomes Pro Annual',
        noTrialReassurance: 'Charged today, annual renewal · cancel anytime',
    },
    winback: {
        countdownLead: 'This offer disappears in',
        timeHm: '{{hours}}h {{minutes}}m',
        timeM: '{{minutes}}m',
        badge: '-{{discount}}%',
        metaPaymentOnce: 'One-time payment via {{store}}',
        metaForever: 'Forever access',
        metaAfter: 'After that, Lifetime goes back to {{regularPrice}}.',
        legalOnce: 'Shown only once.',
        default: {
            title: 'Kontto Pro forever,\nfor a single payment',
            subtitle: 'No subscription, ever again.',
            cta: 'Keep Pro forever',
            decline: 'Not now',
        },
        renewalOff: {
            title: 'Your Pro ends on {{date}}.\nKeep it forever for {{price}}?',
            titleNoDate: 'Your Pro is about to end.\nKeep it forever for {{price}}?',
            subtitle: 'One payment, less than 9 months of the monthly plan. Then never another charge.',
            noDoubleCharge: 'Your monthly plan no longer renews. No double charge.',
            keepEverything: 'Everything you set up stays: themes, widgets, your home screen your way.',
            keepBackup: 'Don\'t lose your data: automatic backup keeps running.',
            keepReports: 'Advanced reports, PDF export and no limits on anything.',
            cta: 'Keep Pro forever',
            switchAnnualLead: 'Rather subscribe?',
            switchAnnualCta: 'Switch to Annual · {{annualPrice}}/year',
            switchAnnualPerMonth: 'that\'s {{perMonth}}/month',
            decline: 'Let it end on {{date}}',
            declineNoDate: 'Let it end',
        },
        churned: {
            title: 'Come back to Pro, for good this time.\n{{price}}, one payment.',
            subtitle: 'No monthly charge to keep an eye on. Pay once and stay.',
            dataSingular: 'Your {{count}} transaction and your reports are right here, just as you left them.',
            dataPlural: 'Your {{count}} transactions and reports are right here, just as you left them.',
            dataFallback: 'Your data and reports are right here, just as you left them.',
            backBackup: 'Automatic backup starts running again: don\'t lose your data.',
            backReports: 'Advanced reports with PDF and no limits, back on.',
            backLook: 'Themes, widgets and your home screen your way.',
            cta: 'Get Pro back forever',
            decline: 'Not now',
        },
        banner: {
            title: 'Pro Lifetime',
            pill: '24 HOURS',
            subtitle: 'Pay once, use forever',
            subtitleRenewalOff: 'Your Pro ends on {{date}}. Keep it forever for {{price}}',
            countdown: 'Ends in {{time}}',
            accessibility: 'Kontto Pro Lifetime for {{price}}. The offer ends in {{time}}.',
        },
        annual: {
            metaFirstYear: 'First year for {{price}} via {{store}}',
            metaCancel: 'Cancel anytime',
            metaAfter: 'Then it renews at {{regularPrice}} per year.',
            default: {
                title: 'Kontto Pro for {{price}}\nfor the first year',
                subtitle: 'That\'s {{perMonth}} a month.',
                cta: 'Subscribe with discount',
            },
            renewalOff: {
                title: 'Your Pro ends on {{date}}.\nStay another year for {{price}}?',
                titleNoDate: 'Your Pro is about to end.\nStay another year for {{price}}?',
                subtitle: 'Swap the monthly plan for the discounted annual one: {{perMonth}} a month.',
                cta: 'Switch to annual with discount',
            },
            churned: {
                title: 'Come back to Pro for {{price}}\nfor the first year',
                subtitle: 'That\'s {{perMonth}} a month, with all of Pro back.',
                cta: 'Back to Pro',
            },
            banner: {
                title: 'Pro Annual',
                subtitle: 'First year at a discount',
                subtitleRenewalOff: 'Your Pro ends on {{date}}. Stay another year for {{price}}',
                accessibility: 'Kontto Pro Annual for {{price}} for the first year. The offer ends in {{time}}.',
            },
        },
    },
    valueMoment: {
        title: 'You\'re in control',
        subtitle: 'Keeping your accounts up to date is the habit that changes the game. Look at your progress:',
        daysSingular: 'day in control',
        daysPlural: 'days in control',
        txSingular: 'transaction recorded',
        txPlural: 'transactions recorded',
        settledSingular: 'bill paid',
        settledPlural: 'bills paid',
        proLineLead: '{{lead}}, Kontto Pro goes further: advanced reports, backup, and the app your way.',
        proLine: 'Kontto Pro takes this further: advanced reports, backup, and the app your way.',
        cta: 'Explore Kontto Pro',
    },
    downgrade: {
        title: 'Your Kontto Pro has expired',
        subtitle: 'From now on:',
        reportBlockedCount: 'Your {{month}} report is locked — with the {{expenses}} expenses you recorded.',
        reportBlocked: 'Your {{month}} report is locked.',
        backupStoppedLast: 'Automatic backup stopped running. The last one was {{distance}}.',
        backupStopped: 'Automatic backup stopped running.',
        themeReset: 'Your theme is back to default.',
        monthStartReset: 'Your month starts on day 1 again.',
        limitsBack: 'Creating accounts, cards, goals, and budgets is limited again.',
        footnote: 'Nothing was deleted: your data and history stay on your device. Reactivate and everything comes back as it was, just how you left it.',
        cta: 'Reactivate Pro',
    },
    trialCancel: {
        title: "Done, you won't be charged",
        subtitle: 'Your Kontto Pro stays unlocked until {{date}}. Enjoy the days you have left.',
        question: 'If you can, tell us: what made you cancel?',
        reasons: {
            forget: 'I only cancelled so I would not forget',
            price: 'It felt expensive',
            use: "I haven't used it enough yet",
            missing: 'Something was missing',
        },
        footnote: 'One tap and done. Your answer helps decide what comes next.',
        notNow: 'Not now',
        close: 'Close',
        today: 'today',
        tomorrow: 'tomorrow',
        understood: 'Got it',
        forget: {
            title: 'Deal',
            body: "We'll remind you {{day}}, one day before it ends. Until then, all of Pro stays unlocked.",
            bodyNoReminder: 'Until {{date}}, all of Pro stays unlocked.',
            today: 'Today',
            todayText: 'Trial cancelled. Nothing will be charged.',
            reminderText: 'Reminder with your trial summary',
            endText: 'Pro ends. Your data stays here.',
            invoiceTitle: 'Meanwhile, check your statement',
            invoiceBody: 'Match each purchase with your bank statement',
            reportTitle: 'Meanwhile, see your report',
            reportBody: 'Categories, cash flow and PDF',
        },
        price: {
            title: "There's a monthly plan",
            body: 'No paying for the whole year at once. Pay month by month and cancel anytime.',
            planName: 'Kontto Pro Monthly',
            badge: 'NO COMMITMENT',
            perMonth: '/month',
            note: "When the trial ends, it'll already be selected for you.",
            footnote: "The trial you cancelled runs until {{date}} and won't be charged.",
        },
        use: {
            title: 'There is still time to try it',
            body_one: '{{count}} day of Pro left. Start with one of these:',
            body_other: '{{count}} days of Pro left. Start with one of these:',
            invoiceTitle: 'Check your statement',
            invoiceBody: 'Match each purchase with your bank statement',
            reportTitle: '{{month}} report',
            reportBody: 'Categories, cash flow and PDF',
            backupTitle: 'Automatic backup',
            backupBody: 'A copy in the folder you choose',
            ctaInvoice: 'Start with the statement',
            ctaReport: 'See my report',
        },
        missing: {
            title: 'What was missing?',
            body: 'Tap what you missed most. It goes straight to our list of what comes next.',
            wants: {
                bank: 'Pull expenses straight from the bank',
                web: 'Use it on a computer',
                share: 'Share with another person',
                sync: 'Sync between phones',
                other: 'Something else',
            },
            hint: '“Something else” opens the feedback screen so you can write to us.',
            send: 'Send',
            write: 'Write to us',
            skip: 'Skip',
            thanksTitle: 'Thanks!',
            thanksBody: "It's on our list.",
        },
    },
    trialSummary: {
        pillLastDay: 'Last day of your trial',
        pillUntil: 'Trial until {{date}}',
        title: 'Your trial in numbers',
        body: 'Your Pro ends {{when}}. This is what you did with it:',
        today: 'today',
        tomorrow: 'tomorrow',
        entries_one: 'entry',
        entries_other: 'entries',
        reconciles_one: 'statement checked',
        reconciles_other: 'statements checked',
        days_one: 'day of Pro',
        days_other: 'days of Pro',
        lossesTitle: 'When Pro ends',
        lossReport: 'Your {{month}} report gets locked',
        lossBackup: 'Automatic backup stops running',
        lossLimits: 'Creating accounts, cards and goals gets limited again',
        footnote: 'Nothing is deleted. Your data stays on your phone.',
        cta: 'Keep Pro',
        ctaCaption: 'Annual: {{annual}} per year ({{perMonth}}/month). Turn renewal back on in the {{store}}.',
        ctaCaptionNoMonth: 'Annual: {{annual}} per year. Turn renewal back on in the {{store}}.',
        otherPlans: 'See other plans',
        storeErrorTitle: "Couldn't open the store right now",
        storeErrorBody: 'Try again in a moment, or turn it back on in {{location}}.',
    },
    // iPhone paused mode (ADR 0176): dock, Home, sheet and More tab.
    paused: {
        dockTitle: 'Your Kontto is paused',
        dockBody_one: '{{count}} entry saved. Reactivate to keep going.',
        dockBody_other: '{{count}} entries saved. Reactivate to keep going.',
        dockBodyNoCount: 'Your data is saved. Reactivate to keep going.',
        dockCta: 'Reactivate',
        dockAccessibility: 'Reactivate Kontto',
        lastEntry_one: 'Last entry on {{date}} · {{count}} day ago',
        lastEntry_other: 'Last entry on {{date}} · {{count}} days ago',
        overlayAccessibility: 'Reactivate Kontto to change your data',
        lockedTab: '{{tab}}, requires Pro',
        lockedAdd: 'New entry, requires Pro',
        pillTrial: 'Trial ended on {{date}}',
        pillPro: 'Pro ended on {{date}}',
        titleSince: 'Since then, Kontto has stopped',
        title: 'Your Kontto is paused',
        body: 'Nothing was deleted. It just stopped keeping track of your money.',
        idleTitle_one: '{{count}} day without entries',
        idleTitle_other: '{{count}} days without entries',
        idleBody: 'The balance here no longer matches your bank.',
        invoiceTitleToday: '{{card}} bill closes today',
        invoiceTitleTomorrow: '{{card}} bill closes tomorrow',
        invoiceTitle: '{{card}} bill closes in {{days}} days',
        invoiceBody: '{{amount}} so far, not reconciled.',
        reportTitle: '{{month}} report locked',
        reportBody: '{{amount}} in expenses, without seeing where it went.',
        backupTitle: 'Automatic backup stopped',
        backupBody: 'The last one was on {{date}}.',
        benefitsTitle: 'With Pro you',
        benefitEntries: 'Add expenses and income with no limit',
        benefitReport: 'See the monthly report by category',
        benefitInvoice: 'Reconcile your credit card bill',
        benefitBackup: 'Get automatic backup of your data',
        planMonthly: 'Monthly',
        planMonthlyHint: 'Cancel anytime',
        planAnnual: 'Annual',
        planTrialHint: '{{period}} free, then {{price}} per year',
        perMonth: '/mo',
        ctaReactivate: 'Reactivate Kontto',
        ctaTrial: 'Start free trial',
        captionMonthly: '{{price}} per month, renews automatically. Cancel anytime in {{location}}.',
        captionTrial:
            '{{period}} free, then {{price}} per year, renews automatically. Cancel before the trial ends in {{location}} and pay nothing.',
        allPlans: 'See all plans',
        restore: 'Restore purchase',
        terms: 'Terms',
        privacy: 'Privacy',
        close: 'Close',
        moreOpenSection: 'Works without Pro',
        moreLockedSection: 'With Pro',
        moreSettings: 'Settings',
        moreFeedback: 'Talk to me',
        moreRestore: 'Restore purchase',
        moreAccounts: 'Accounts and cards',
        moreCategories: 'Categories and tags',
        morePlanning: 'Budgets and goals',
        moreImport: 'Import statement',
        moreBackup: 'Backup and sync',
        lockedBadge: 'Pro',
    },
    banner: {
        title: 'Kontto Pro',
        subtitleTrial: 'Try it free: advanced reports, themes, and everything unlimited',
        subtitle: 'Advanced reports, themes, and everything unlimited',
        trialAccessibility: 'Try Kontto Pro free for {{period}}',
    },
    chips: {
        moreFeatures: '+ {{num}} features',
        labels: {
            reports: 'Reports + PDF',
            unlimited: 'Everything unlimited',
            sync: 'Sync',
        },
    },
    featuresSheet: {
        title: 'Everything Pro includes',
    },
    activation: {
        registered: 'recorded',
    },
    proPush: {
        tenureWeeks_one: 'Your {{count}} week of Kontto',
        tenureWeeks_other: 'Your {{count}} weeks of Kontto',
        tenureMonths_one: 'Your {{count}} month of Kontto',
        tenureMonths_other: 'Your {{count}} months of Kontto',
        statTransactions_one: 'entry',
        statTransactions_other: 'entries',
        statAccounts_one: 'account',
        statAccounts_other: 'accounts',
        statCards_one: 'card',
        statCards_other: 'cards',
        trialTitleLead: 'Try Pro',
        trialTitleHighlight: 'for free',
        comebackTitleLead: 'Your Kontto,',
        comebackTitleHighlight: 'complete again',
        limitsHook: 'You already do the hard part: writing it all down.',
        backupHook: 'Right now, all of this only lives on this phone.',
        benefitUnlimited: 'No limits',
        benefitHome: 'Your home screen, your way',
        benefitDebts: 'Who owes me',
        benefitBackup: 'Automatic backup',
        benefitSync: 'Sync across devices',
        monthlyTitle: 'Monthly',
        monthlySub: '{{monthly}} a month',
        annualTrialTitle: '{{period}} free',
        annualTrialSub: 'then {{annual}} a year · {{perMonth}}/month',
        annualTrialSubNoMonth: 'then {{annual}} a year',
        annualTitle: 'Get Pro Annual',
        annualSub: '{{annual}} a year · {{perMonth}}/month',
        annualSubNoMonth: '{{annual}} a year',
        savingsBadge: 'SAVE {{pct}}%',
        trialFinePrint: 'No charge during the trial · we remind you {{days}} days before it ends · cancel anytime on the {{store}}',
        finePrint: 'Renews automatically · cancel anytime on the {{store}}',
        plansUnavailable: "Couldn't load the plans right now. Try again later.",
    },
    gate: {
        hiddenAmount: 'Amount available with Pro',
        limitTitle: 'Kontto Pro feature',
        limitMessage: 'On the free plan you can have up to {{limit}} {{label}}. With Pro, create as many as you want.',
        limits: {
            accounts: 'accounts',
            creditCards: 'cards',
            categories: 'custom categories',
            budgets: 'budgets',
            goals: 'goals and pockets',
            tags: 'tags',
            subcategories: 'subcategories per category',
            financings: 'active contracts (financing or loan)',
        },
        badge: 'PRO',
    },
} satisfies DeepPartialStrings<TranslationSchema['pro']>;
